import { useCallback, useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import errorMonitoring from 'errorMonitoring/ErrorMonitoring';
import { isPayPalV6Enabled } from 'utils/paypalV6Config';
import {
  quickPurchaseMainButtonTracker,
  quickPurchasePaymentAttemptTracker,
} from 'metrics/user-analytics/purchase';
import { isAddiBlockedByDocument } from 'utils/purchase/addiDocumentValidation';
import buildPaymentAttemptDataAudit from 'utils/purchase/paymentAttemptDataAudit';
import selectIsPurchaserFormValid from 'utils/purchase/selectIsPurchaserFormValid';
import basisTheoryCheckoutEngine from 'payments/core/engines/basisTheory';
import usePaymentEngines from './usePaymentEngines';
import usePaymentHandler from './usePaymentHandler';

/**
 * PaymentOptions passes payment params; PaymentButton passes the click event.
 * @param {object|Event} paymentParams - Payment params or DOM click event.
 * @returns {object} Normalized payment params.
 */
const normalizePaymentClickParams = (paymentParams) => {
  if (paymentParams && typeof paymentParams === 'object' && 'nativeEvent' in paymentParams) {
    return {};
  }

  return paymentParams || {};
};

/**
 * Indicates whether the payment option uses a native wallet sheet (Apple Pay / Google Pay).
 * @param {string} paymentOption - Selected payment option.
 * @returns {boolean} Whether the option is a wallet payment.
 */
const isWalletPaymentOption = (paymentOption) =>
  paymentOption === 'apple_pay' || paymentOption === 'google_pay';

/**
 * usePaymentSection hook.
 * Consolidate all payment-related state and handlers for the payment section UI.
 * Internally initializes payment engine hooks and the payment handler, exposing
 * only the callbacks and flags needed for rendering PaymentPanel.
 * @param {object} params - Hook parameters.
 * @param {object} params.purchase - Purchase state object (includes token, total, selectedPaymentOption, etc.).
 * @param {object} params.env - Environment configuration.
 * @param {object} params.features - Whitelabel feature flags.
 * @param {object} params.purchaser - Purchaser data derived from form/store.
 * @param {object} params.purchaserFormValues - Raw redux-form values for the purchaser form.
 * @param {object} params.actions - Action callbacks from useQuickCheckoutActions.
 * @param {object} params.cardValues - Card form values.
 * @param {boolean} params.fetchingPayment - Whether a payment confirmation is in flight.
 * @param {boolean} params.isUpdating - Whether the purchase is being updated.
 * @param {object} params.selectedPaymentMethod - Currently selected payment method.
 * @param {'basis_theory'|'legacy'} params.cardCheckoutMode - Owner of card capture and submission.
 * @param {string} params.paymentEngine - Payment engine identifier.
 * @param {boolean} params.isOrchestrating - Whether quick checkout setup is in progress.
 * @param {object} params.orchestrationError - Quick checkout setup error, if any.
 * @param {boolean} params.purchaserSubmitted - Whether the purchaser form has been submitted (owned by parent).
 * @param {Function} params.onPurchaserSubmitted - Callback to notify the parent that the purchaser was saved.
 * @param {Function} [params.onBeforePaymentAsync] - Optional async callback that must resolve before payment proceeds.
 * @returns {object} Payment section handlers and loading flag.
 */
function usePaymentSection({
  purchase,
  env,
  features,
  purchaser,
  purchaserFormValues,
  actions,
  cardValues,
  fetchingPayment,
  isUpdating,
  selectedPaymentMethod,
  cardCheckoutMode,
  paymentEngine,
  isOrchestrating,
  orchestrationError,
  purchaserSubmitted,
  onPurchaserSubmitted,
  onBeforePaymentAsync,
}) {
  const { token, total, updatingInsurance, selectedPaymentOption } = purchase;
  // Registration-independent buyer validity, same selector the regular Checkout and the wallet
  // buttons gate on (CheckoutContainer, WalletButtonContainer). `purchaserSubmitted` cannot stand in
  // for it: it only means "already saved", never "valid".
  const isPurchaserFormValid = useSelector(selectIsPurchaserFormValid);
  const [isSyncingPurchaserForWallet, setIsSyncingPurchaserForWallet] = useState(false);
  const [basisTheoryStatus, setBasisTheoryStatus] = useState(basisTheoryCheckoutEngine.getStatus());
  const pendingPaymentParamsRef = useRef(null);
  const pendingWalletPaymentParamsRef = useRef(null);
  const autoSubmittedPurchaserRef = useRef(false);
  // Pending payment params for the passenger-edit path. Unlike the synchronous
  // purchaser path, payment here must resume AFTER the passenger save mutates the
  // store and re-renders QuickCheckout. Resuming inside the onBeforePaymentAsync
  // .then() would run continuePayment/handlePayment with stale click-time closures
  // (old purchase, engineHooks, isUpdating), so the payment silently no-ops. We defer
  // to an effect that fires from a fresh render once payment is unblocked.
  const pendingAfterPassengersParamsRef = useRef(null);
  const [pendingAfterPassengers, setPendingAfterPassengers] = useState(false);
  const { engineHooks, enginesLoading } = usePaymentEngines({ purchase, env, purchaser, actions });
  const { handlePayment, isProcessing } = usePaymentHandler({
    purchase: {
      ...purchase,
      isUpdating: isUpdating || updatingInsurance,
      selectedPaymentMethod,
      cardCheckoutMode,
      paymentEngine,
    },
    env,
    purchaser,
    actions,
    engineHooks,
  });
  // Drives the button spinner (isLoading): any in-flight/updating state.
  const basisTheoryLoading =
    cardCheckoutMode === 'basis_theory' &&
    (basisTheoryStatus === 'loading' || basisTheoryStatus === 'submitting');
  const basisTheoryUnavailable =
    cardCheckoutMode === 'basis_theory' && basisTheoryStatus !== 'ready';
  const buttonLoading =
    fetchingPayment ||
    isUpdating ||
    enginesLoading ||
    isProcessing ||
    isSyncingPurchaserForWallet ||
    updatingInsurance ||
    basisTheoryLoading;

  // Addi (Yuno BNPL) only accepts Cédula de Ciudadanía. Folded into
  // paymentBlocked (not buttonLoading) so it disables the pay button and guards
  // the payment handlers without turning on the spinner, which tracks
  // buttonLoading only.
  const addiBlocked = isAddiBlockedByDocument({
    selectedPaymentMethod,
    purchaserDocumentType: purchaserFormValues?.documentType,
  });
  // Drives the disabled state (isDisabled) and the internal payment guards:
  // either something is loading or the payment is not allowed to proceed.

  const paymentBlocked = buttonLoading || basisTheoryUnavailable || addiBlocked;
  useEffect(() => basisTheoryCheckoutEngine.subscribe(setBasisTheoryStatus), []);

  // Reports what the purchase actually holds at the moment of a payment attempt. Placed in
  // continuePayment because it is the single point every payment type passes through — the PayPal v6
  // and wallet branches below return before handlePayment, so reporting there would miss them.
  // Always tracks (an incomplete rate needs a denominator) and only alerts when something is off.
  // Fail-open: a diagnostic must never be the reason a payment does not go through.
  const reportPaymentAttemptData = useCallback(() => {
    try {
      const audit = buildPaymentAttemptDataAudit({ purchase, purchaserFormValues });

      quickPurchasePaymentAttemptTracker({
        token,
        paymentOption: selectedPaymentOption,
        ...audit,
      });

      if (audit.hasIncompleteData) {
        errorMonitoring.notify({
          error: new Error('Quick purchase payment attempt with incomplete buyer/passenger data'),
          info: {
            from: 'usePaymentSection:paymentAttempt',
            token,
            paymentOption: selectedPaymentOption,
            missingFields: audit.missingFields,
            unsavedBuyerFields: audit.unsavedBuyerFields,
            buyer: audit.buyer,
            buyerForm: audit.buyerForm,
            passengers: audit.passengers,
          },
        });
      }
    } catch (error) {
      // The reporter of a reporting failure can fail too (a broken transport takes both the tracker
      // and notify down together). Swallow it: nothing here is worth blocking a payment for.
      try {
        errorMonitoring.notify({
          error,
          info: { from: 'usePaymentSection:reportPaymentAttemptData', token },
        });
      } catch (notifyError) {
        console.warn('payment attempt reporting failed', notifyError);
      }
    }
  }, [purchase, purchaserFormValues, selectedPaymentOption, token]);

  const continuePayment = useCallback(
    (paymentParams = {}) => {
      reportPaymentAttemptData();

      const isPaypalV6 = selectedPaymentOption === 'paypal' && isPayPalV6Enabled();

      if (isPaypalV6) {
        const { paypalType, paypalOptions = {} } = paymentParams;
        actions.onPaypalPaymentAttempt(paypalType, paypalOptions);
        return;
      }

      if (isWalletPaymentOption(selectedPaymentOption)) {
        if (paymentParams.resolve) {
          paymentParams.resolve();
        } else {
          console.warn(`${selectedPaymentOption}: paymentParams.resolve is missing`);
        }
        return;
      }

      handlePayment();
    },
    [actions, handlePayment, reportPaymentAttemptData, selectedPaymentOption],
  );

  const handlePaymentClick = useCallback(
    (paymentParams = {}) => {
      const params = normalizePaymentClickParams(paymentParams);
      const isWalletPayment = isWalletPaymentOption(selectedPaymentOption);
      const showPurchaserForm = features.PURCHASER_FORM_ON === 'CHECKOUT';
      const shouldSyncPurchaserBeforeWallet =
        showPurchaserForm &&
        !purchaserSubmitted &&
        !params.skipPurchaserCheck &&
        isWalletPayment &&
        typeof params.resolve === 'function' &&
        typeof params.reject === 'function';

      if (paymentBlocked) {
        params.reject?.();
        pendingPaymentParamsRef.current = null;
        return;
      }

      quickPurchaseMainButtonTracker();

      // Buyer validity gate. It must run BEFORE the onBeforePaymentAsync branch below: that branch
      // returns early and later resumes with `skipPurchaserCheck: true`, so the purchaser submit —
      // and with it `purchaserValidations` — was never reached while the passengers form was open
      // (opened by the user, or automatically for incomplete passenger data per CON-1076). Payment
      // then went through with a blank buyer phone and failed downstream. Gating here also means an
      // invalid buyer never triggers the passenger save, so the purchase is not left partially
      // updated. Mirrors the wallet gate in Checkout.js, including its `=== false` check: only a
      // positive invalid result blocks, so an uncomputable validity preserves prior behavior rather
      // than locking the button.
      if (showPurchaserForm && !params.skipPurchaserCheck && isPurchaserFormValid === false) {
        params.reject?.();
        pendingPaymentParamsRef.current = null;
        // Submitting marks the fields touched so they render in error state; onFormFail raises the
        // "check the fields in red" warning.
        actions.onSubmitForm('purchaser');
        actions.onFormFail();
        return;
      }

      if (onBeforePaymentAsync) {
        onBeforePaymentAsync()
          .then(() => {
            // Passenger save already handled the purchaser update inside the thunk.
            // Defer the actual payment to the effect below so it resumes from a fresh
            // render with up-to-date state/handlers instead of these stale closures.
            pendingAfterPassengersParamsRef.current = params;
            setPendingAfterPassengers(true);
          })
          .catch((error) => {
            errorMonitoring.notify({
              error,
              info: { from: 'usePaymentSection:onBeforePaymentAsync' },
            });
            params.reject?.(error);
          });
        return;
      }

      if (showPurchaserForm && !purchaserSubmitted && !params.skipPurchaserCheck) {
        if (shouldSyncPurchaserBeforeWallet) {
          pendingWalletPaymentParamsRef.current = {
            resolve: params.resolve,
            reject: params.reject,
          };
          setIsSyncingPurchaserForWallet(true);
          actions.onSubmitForm('purchaser');
          return;
        }

        if (isWalletPayment && params.reject) {
          params.reject();
        }

        pendingPaymentParamsRef.current = params;
        actions.onSubmitForm('purchaser');
        if (isWalletPayment) {
          actions.onFormFail();
        }
        return;
      }

      continuePayment(params);
    },
    [
      actions,
      continuePayment,
      features.PURCHASER_FORM_ON,
      isPurchaserFormValid,
      paymentBlocked,
      purchaserSubmitted,
      onBeforePaymentAsync,
      selectedPaymentOption,
    ],
  );

  const handleSubmitPurchaser = useCallback(() => {
    const pendingWallet = pendingWalletPaymentParamsRef.current;
    pendingWalletPaymentParamsRef.current = null;

    actions
      .onSetPurchaser(token, purchaserFormValues)
      .then(() => {
        if (pendingWallet) {
          setIsSyncingPurchaserForWallet(false);
          pendingPaymentParamsRef.current = {
            skipPurchaserCheck: true,
            resolve: pendingWallet.resolve,
            reject: pendingWallet.reject,
          };
        }

        onPurchaserSubmitted?.();
      })
      .catch((error) => {
        setIsSyncingPurchaserForWallet(false);
        pendingPaymentParamsRef.current = null;

        if (pendingWallet?.reject) {
          pendingWallet.reject();
        }

        errorMonitoring.notify({
          error,
          info: { from: 'usePaymentSection:submitPurchaser', token },
        });
      });
  }, [actions, token, purchaserFormValues, onPurchaserSubmitted]);

  const handlePurchaserSubmitFail = useCallback(() => {
    const pendingWallet = pendingWalletPaymentParamsRef.current;
    pendingWalletPaymentParamsRef.current = null;
    setIsSyncingPurchaserForWallet(false);
    pendingPaymentParamsRef.current = null;

    if (pendingWallet?.reject) {
      pendingWallet.reject();
    }
  }, []);

  const handleCardSubmit = useCallback(() => {
    const paymentInfo = {
      amount: total,
      currency: env.currency,
    };
    actions.onCardPaymentAttempt(token, cardValues, paymentInfo);
  }, [total, env.currency, actions, token, cardValues]);

  const handleTransferSubmit = useCallback(
    (transferData) => {
      actions.onTransferPaymentAttempt(transferData);
    },
    [actions],
  );

  const handlePaymentTypeChange = useCallback(
    (option, method) => {
      actions.onSelectPaymentOption(option, method);
    },
    [actions],
  );

  // Pre-submit the purchaser form when PayPal v6 is selected so the SDK button works in a
  // single click. PayPal's popup must open within the user-activation window of the click;
  // routing the click through an async purchaser update consumes that window and the popup
  // is suppressed silently — forcing the user to click twice.
  useEffect(() => {
    if (isOrchestrating || orchestrationError) return;
    if (purchaserSubmitted || autoSubmittedPurchaserRef.current) return;
    if (!(selectedPaymentOption === 'paypal' && isPayPalV6Enabled())) return;
    if (!purchaserFormValues?.purchaserFirstName) return;
    autoSubmittedPurchaserRef.current = true;
    actions.onSubmitForm('purchaser');
  }, [
    isOrchestrating,
    orchestrationError,
    purchaserSubmitted,
    selectedPaymentOption,
    purchaserFormValues,
    actions,
  ]);

  useEffect(() => {
    if (!purchaserSubmitted || paymentBlocked || !pendingPaymentParamsRef.current) return;

    const pendingPaymentParams = pendingPaymentParamsRef.current;
    pendingPaymentParamsRef.current = null;

    continuePayment({
      ...pendingPaymentParams,
      skipPurchaserCheck: true,
    });
  }, [continuePayment, paymentBlocked, purchaserSubmitted]);

  // Resume payment after a passenger edit save once the store has settled and payment is
  // unblocked. Runs from a fresh render so continuePayment/handlePayment use the latest
  // purchase, engineHooks and loading flags instead of stale click-time closures. The save
  // relock preserves the payment selection (preservePaymentOptionSelection in getPurchase),
  // so the option/card form survive. The purchaser update was already handled inside the
  // passenger save thunk, so skip its check.
  useEffect(() => {
    if (!pendingAfterPassengers || paymentBlocked) return;

    const pendingPaymentParams = pendingAfterPassengersParamsRef.current || {};
    pendingAfterPassengersParamsRef.current = null;
    setPendingAfterPassengers(false);

    continuePayment({
      ...pendingPaymentParams,
      skipPurchaserCheck: true,
    });
  }, [continuePayment, paymentBlocked, pendingAfterPassengers]);

  return {
    handlePaymentClick,
    handleSubmitPurchaser,
    handlePurchaserSubmitFail,
    handleCardSubmit,
    handleTransferSubmit,
    handlePaymentTypeChange,
    buttonLoading,
    paymentBlocked,
  };
}

export default usePaymentSection;
